import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DevMonitor from './pages/DevMonitor';
import AdminLayout from './layouts/AdminLayout';
import SupportLayout from './layouts/SupportLayout';
import AccountLayout from './layouts/AccountLayout';
import UserLayout from './layouts/UserLayout';

// Master Admin Route Guard (for Dev Tools only)
import MasterAdminRoute from './components/MasterAdminRoute';

// Protected Route Component - All authenticated users
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Role-based Layout Selector - Multi-role support
const RoleBasedLayout = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case 'master_admin':
      return <AdminLayout />;
    case 'support':
      return <SupportLayout />;
    case 'account':
      return <AccountLayout />;
    case 'user':
      return <UserLayout />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// Create Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Dev Monitor - Master Admin Only (security best practice) */}
            <Route 
              path="/dev/monitor" 
              element={
                <MasterAdminRoute>
                  <DevMonitor />
                </MasterAdminRoute>
              } 
            />
            
            {/* All other routes - Role-based access */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <RoleBasedLayout />
                </ProtectedRoute>
              }
            />
            
                      
            {/* Unauthorized route */}
            <Route path="/unauthorized" element={
              <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-800 mb-4">403</h1>
                  <p className="text-xl text-gray-600">Unauthorized Access</p>
                </div>
              </div>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
